import React, { useState, useRef, useEffect } from 'react';
import s from './LazyImage.module.css';

/**
 * Drop-in replacement for <img> that shows a shimmer skeleton until loaded.
 * Renders as a React Fragment — no extra wrapper div, so parent CSS selectors
 * (e.g. `.wwaCardImg img`) still match the rendered <img>.
 *
 * The parent container MUST have `position: relative` (or any non-static
 * position) so the absolutely-positioned skeleton fills it correctly.
 */
export default function LazyImage({ src, alt, className = '', priority = false, ...imgProps }) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  // Fire immediately for already-cached images (no onLoad event fired)
  useEffect(() => {
    if (ref.current?.complete) setLoaded(true);
  }, [src]);

  // Priority images (LCP): no skeleton, no opacity transition, eager + high priority
  if (priority) {
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={className}
        loading="eager"
        fetchPriority="high"
        {...imgProps}
      />
    );
  }

  return (
    <>
      {!loaded && <span className={s.skeleton} aria-hidden="true" />}
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={`${className} ${s.img} ${loaded ? s.loaded : ''}`.trim()}
        onLoad={() => setLoaded(true)}
        {...imgProps}
      />
    </>
  );
}
